import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  useTheme,
  CircularProgress,
  Alert,
  Collapse,
  Chip,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Info as InfoIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Lock as LockIcon,
  DateRange as DateRangeIcon,
  Warning as WarningIcon,
  Edit as EditIcon,
  PersonAdd as PersonAddIcon,
  Settings as SettingsIcon,
  Calculate as CalculateIcon,
  ArrowUpward as MoveUpIcon,
  ArrowDownward as MoveDownIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import axios from 'axios';
import { debounce } from 'lodash';
import * as api from '../../api/api';
import itRosterData from '../../data/it_roster.json';
import SummaryCards from './SummaryCards';
import RoleTimelineModal from './shared/RoleTimelineModal';
import TimelineAdjustmentModal from './shared/TimelineAdjustmentModal';
import AddMemberModal from '../shared/AddMemberModal';
import {
  getOutcomeTimelineDisplay,
  ensureRoleFields,
  getEffectiveDateRanges,
  calculateTotalCurrentRoleCosts,
  calculateMonthlyBreakdown,
  convertDailyToMonthly,
  roleToFunctionMap,
  roleToFocusMap,
  getFilteredRosterForRole,
  BUSINESS_DAYS_PER_MONTH,
  CAPEX_THRESHOLD
} from '../../utils/wizardHelpers';
import { getRateForRole, getProjectYear, calculateMemberSalary } from '../../utils/rateUtils';

// Memoized Autocomplete component to prevent unnecessary re-renders
const MemoizedAutocomplete = React.memo(({ role, itRoster, onRosterMemberChange, readOnly }) => {
  // Memoize the filtered options to prevent recalculation on every render
  const filteredOptions = useMemo(() => {
    return getFilteredRosterForRole(itRoster, role.function);
  }, [itRoster, role.function]);

  // Memoize the selected value
  const selectedValue = useMemo(() => {
    return itRoster.find(member => member.id === role.roster_member_id) || null;
  }, [itRoster, role.roster_member_id]);

  // Memoize the filter function
  const filterOptions = useCallback((options, { inputValue }) => {
    return getFilteredRosterForRole(itRoster, role.function, inputValue);
  }, [itRoster, role.function]);

  const getOptionLabel = useCallback((option) => {
    if (typeof option === 'string') return option;
    const name = option.name || `${option.first_name || ''} ${option.last_name || ''}`.trim();
    return name || 'Unknown';
  }, []);

  const renderOption = useCallback((props, option) => (
    <Box component="li" {...props}>
      <Box>
        <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
          {option.name || `${option.first_name || ''} ${option.last_name || ''}`.trim()}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
          {option.function} - Rate calculated dynamically
        </Typography>
      </Box>
    </Box>
  ), []);

  const renderInput = useCallback((params) => (
    <TextField
      {...params}
      placeholder="Search..."
      variant="outlined"
      sx={{ '& .MuiInputBase-input': { fontSize: '0.75rem' } }}
    />
  ), []);

  return (
    <Autocomplete
      size="small"
      options={filteredOptions}
      getOptionLabel={getOptionLabel}
      renderOption={renderOption}
      value={selectedValue}
      onChange={(event, newValue) => onRosterMemberChange(newValue)}
      renderInput={renderInput}
      disabled={readOnly}
      filterOptions={filterOptions}
      sx={{ minWidth: 180, '& .MuiAutocomplete-input': { fontSize: '0.75rem' } }}
    />
  );
});

MemoizedAutocomplete.displayName = 'MemoizedAutocomplete';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

const defaultRoles = ["Product", "Tech Lead", "BA", "UX", "FE", "BE", "QA", "Other"];

const sizeDefs = {
  Small: "A small team can deliver in a quarter, the outcome builds off of existing functionality and has minimal dependencies.",
  Medium: "A small team can deliver in two quarters, the outcome is a simple but brand new feature with minimal dependencies.",
  Large: "A large team can deliver in two quarters, the outcome builds off of existing functionality but has several dependencies.",
  "Extra Large": "A large team can deliver in a year, the outcome is new functionality with several dependencies or builds off of existing functionality but is broad in scope."
};

const sizeMonths = { Small: 3, Medium: 6, Large: 6, "Extra Large": 12 };
const confidenceMultipliers = { Low: 1.4, Medium: 1.2, High: 1.0 };

const sizeRoleMatrix = {
  Small: {
    Product: { count: 1, percent: 25 },
    "Tech Lead": { count: 1, percent: 50 },
    BA: { count: 0, percent: 0 },
    UX: { count: 1, percent: 25 },
    FE: { count: 1, percent: 100 },
    BE: { count: 1, percent: 100 },
    QA: { count: 1, percent: 25 },
    Other: { count: 0, percent: 0 }
  },
  Medium: {
    Product: { count: 1, percent: 50 },
    "Tech Lead": { count: 1, percent: 75 },
    BA: { count: 1, percent: 50 },
    UX: { count: 1, percent: 50 },
    FE: { count: 1, percent: 100 },
    BE: { count: 1, percent: 100 },
    QA: { count: 1, percent: 50 },
    Other: { count: 0, percent: 0 }
  },
  Large: {
    Product: { count: 1, percent: 75 },
    "Tech Lead": { count: 1, percent: 100 },
    BA: { count: 1, percent: 75 },
    UX: { count: 1, percent: 75 },
    FE: { count: 2, percent: 100 },
    BE: { count: 2, percent: 100 },
    QA: { count: 1, percent: 75 },
    Other: { count: 1, percent: 50 }
  },
  "Extra Large": {
    Product: { count: 1, percent: 100 },
    "Tech Lead": { count: 1, percent: 100 },
    BA: { count: 1, percent: 100 },
    UX: { count: 1, percent: 100 },
    FE: { count: 3, percent: 100 },
    BE: { count: 3, percent: 100 },
    QA: { count: 2, percent: 100 },
    Other: { count: 1, percent: 75 }
  }
};

function CostEstimationWizard({
  outcomeId,
  data,
  outcomeStartDate,
  outcomeName,
  outcomeData,
  onUpdate,
  onSave,
  buttonStyle,
  secondaryButtonStyle,
  autoSave = true,
  hideButtons = false,
  readOnly = false
}) {
  const theme = useTheme();
  const initializedRef = useRef(false);
  const lastSavedDataRef = useRef(null);
  
  const getStartMonth = () => {
    if (outcomeStartDate) {
      return dayjs(outcomeStartDate).format('YYYY-MM');
    }
    if (data?.start_month) {
      return data.start_month;
    }
    return dayjs().format('YYYY-MM');
  };

    // Initialize form data with useMemo to prevent unnecessary recreations
  const initialFormData = useMemo(() => {
    const initial = {
      outcome_id: outcomeId,
      outcome_name: outcomeName || '',
      size: data?.size || 'Medium',
      confidence: data?.confidence || 'Medium',
      months: data?.months || 6,
      adjustedMonths: data?.adjustedMonths || 6,
      start_month: data?.start_month || getStartMonth(),
      monthsManuallySet: data?.monthsManuallySet || false,
      roles: data?.roles || [],
      rates: data?.rates || {
        CA: Math.round(163000 / 12),
        Incedo: 8000,
        GFT: 10000
      },
      fixedCosts: data?.fixedCosts || [],
      staggeredStart: data?.staggeredStart || false,
      capexRatio: data?.capexRatio !== undefined ? data.capexRatio : 100,
      capexOverride: data?.capexOverride || false,
      capexAmount: data?.capexAmount || 0,
      opexAmount: data?.opexAmount || 0,
      isCustomized: data?.isCustomized || false,
      useTeamRecommendation: data?.useTeamRecommendation !== undefined ? data.useTeamRecommendation : true,
      ...data
    };
    return {
      ...initial,
      roles: ensureRoleFields(initial.roles, initial.adjustedMonths)
    };
  }, [outcomeId, outcomeName, data, getStartMonth]);

  const [formData, setFormData] = useState(initialFormData);

    const [itRoster, setItRoster] = useState([]);
  const [summary, setSummary] = useState(null);
  const [fixedCosts, setFixedCosts] = useState(formData.fixedCosts || []);
  const [rateData, setRateData] = useState([]);
  const [locationOptions, setLocationOptions] = useState({ 'CA': [] });
  const [roleOptions, setRoleOptions] = useState({ 'CA': { 'US': [] } });
  const [dataLoaded, setDataLoaded] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [timelineModalOpen, setTimelineModalOpen] = useState(false);
  const [pendingTimelineChange, setPendingTimelineChange] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [renderCount, setRenderCount] = useState(0);
  const [autoSaveDisabled, setAutoSaveDisabled] = useState(false);
  const [lastSaveTime, setLastSaveTime] = useState(0);

  // State for role timeline editing
  const [roleTimelineModalOpen, setRoleTimelineModalOpen] = useState(false);
  const [editingRole, setEditingRole] = useState(null);

  // State for collapsible sections
  const [teamRecommendationExpanded, setTeamRecommendationExpanded] = useState(true);
  const [staggeredStartExpanded, setStaggeredStartExpanded] = useState(true);

  // Add state for AddMemberModal
  const [addMemberModalOpen, setAddMemberModalOpen] = useState(false);

  // Add role dialog state
  const [addRoleDialogOpen, setAddRoleDialogOpen] = useState(false);
  const [newRoleForm, setNewRoleForm] = useState({
    name: 'Product',
    company: 'CA',
    location: 'US',
    function: 'Product Manager',
    role: '',
    rate: 0
  });

  // Add role edit dialog state
  const [editRoleDialogOpen, setEditRoleDialogOpen] = useState(false);
  const [editingRoleMetadata, setEditingRoleMetadata] = useState(null);
  const [editRoleForm, setEditRoleForm] = useState({
    name: '',
    company: '',
    location: '',
    function: '',
    role: '',
    rate: 0
  });

  // Helper functions for CAPEX/OPEX logic
  const getTotalProjectCost = () => {
    const baseRoleCost = calculateTotalCurrentRoleCosts(formData);
    const totalFixedCost = (fixedCosts || []).reduce((sum, cost) => {
      return sum + (cost.monthlyCost || 0) * (cost.duration || formData.adjustedMonths);
    }, 0);
    return baseRoleCost + totalFixedCost;
  };

  const isUnderCapexThreshold = () => {
    return getTotalProjectCost() < CAPEX_THRESHOLD;
  };

    // Check if roles have been manually modified
  const hasManualRoleChanges = () => {
    return formData.isCustomized || (formData.roles && formData.roles.length > 0 && 
           formData.roles.some(role => 
             role.roster_member_id || 
             role.useDefaultTimeline === false ||
             role.rate !== Math.round(formData.rates["CA"] || Math.round(163000 / 12))
           ));
  };

  // Check for member over-allocation - memoized
  const getMemberOverAllocation = useCallback((memberId) => {
    if (!formData.roles || !memberId) return { hasOverAllocation: false };
    
    // Calculate total allocation for this member across all roles
    const memberRoles = formData.roles.filter(role => role.roster_member_id === memberId);
    const totalAllocation = memberRoles.reduce((sum, role) => sum + (role.defaultAllocation || 0), 0);
    
    return {
      hasOverAllocation: totalAllocation > 100,
      totalAllocation,
      roleCount: memberRoles.length
    };
  }, [formData.roles]);

      // Check if there are any validation errors
  const hasValidationErrors = () => {
    return Object.keys(validationErrors).some(key => validationErrors[key] && validationErrors[key].length > 0);
  };

    // Track when data is being refreshed from parent
  useEffect(() => {
    if (data && data.last_updated) {
      const currentTime = Date.now();
      const lastUpdated = new Date(data.last_updated).getTime();
      // If data was updated very recently (within 5 seconds), it's likely from a refresh
      if (currentTime - lastUpdated < 5000) {
        setIsRefreshing(true);
        setTimeout(() => setIsRefreshing(false), 1000);
      }
    }
  }, [data?.last_updated]);

  // Track render count to detect infinite loops
  useEffect(() => {
    const newCount = renderCount + 1;
    setRenderCount(newCount);
    
    // If we're rendering too frequently, disable auto-save temporarily
    if (newCount > 10 && !autoSaveDisabled) {
      console.warn('CostEstimationWizard: Too many renders detected, temporarily disabling auto-save');
      setAutoSaveDisabled(true);
      // Re-enable after 5 seconds
      setTimeout(() => {
        console.log('CostEstimationWizard: Re-enabling auto-save after render loop');
        setAutoSaveDisabled(false);
        setRenderCount(0);
      }, 5000);
    }
    
    // Reset render count after 1 second of no renders
    const resetTimer = setTimeout(() => {
      setRenderCount(0);
    }, 1000);
    
    return () => clearTimeout(resetTimer);
  }, []); // Empty dependency array to prevent infinite updates

  // Helper function to check if data has actually changed - with better serialization and ignoring timestamps
  const hasDataChanged = useCallback((newData) => {
    // Create a stable serialization by sorting keys and removing functions and timestamps
    const serializeData = (data) => {
      return JSON.stringify(data, (key, value) => {
        if (typeof value === 'function') return undefined;
        // Ignore timestamp fields that change on every save
        if (key === 'last_updated' || key === 'created_date') return undefined;
        if (value && typeof value === 'object' && !Array.isArray(value)) {
          // Sort object keys for consistent serialization
          const sorted = {};
          Object.keys(value).sort().forEach(k => {
            // Skip timestamp fields in nested objects too
            if (k !== 'last_updated' && k !== 'created_date') {
              sorted[k] = value[k];
            }
          });
          return sorted;
        }
        return value;
      });
    };
    
    const newDataString = serializeData(newData);
    const hasChanged = lastSavedDataRef.current !== newDataString;
    
    if (hasChanged) {
      console.log('CostEstimationWizard: Data change detected (ignoring timestamps)');
      // Log what changed for debugging
      if (lastSavedDataRef.current) {
        try {
          const oldData = JSON.parse(lastSavedDataRef.current);
          const newDataParsed = JSON.parse(newDataString);
          console.log('CostEstimationWizard: Old data keys:', Object.keys(oldData));
          console.log('CostEstimationWizard: New data keys:', Object.keys(newDataParsed));
          
          // Check for specific differences
          const oldRoles = oldData.roles || [];
          const newRoles = newDataParsed.roles || [];
          if (oldRoles.length !== newRoles.length) {
            console.log('CostEstimationWizard: Role count changed:', oldRoles.length, '->', newRoles.length);
          }
        } catch (e) {
          console.log('CostEstimationWizard: Error parsing data for comparison:', e);
        }
      }
    }
    
    return hasChanged;
  }, []);

      // Create stable dependency keys for memoization
  const rolesCostKey = useMemo(() => {
    return formData.roles?.map(r => 
      `${r.id}-${r.defaultAllocation}-${r.rate}-${r.useDefaultTimeline}-${r.dateRanges?.length}`
    ).join(',') || '';
  }, [formData.roles]);
  
  const fixedCostKey = useMemo(() => {
    return formData.fixedCosts?.map(c => `${c.id}-${c.monthlyCost}-${c.duration}`).join(',') || '';
  }, [formData.fixedCosts]);

  // Create a stable debounced update function with longer delay
  const debouncedUpdate = useMemo(
    () => debounce(async (data) => {
      if (onUpdate && !readOnly && autoSave && !saving && !autoSaveDisabled) {
        try {
          setSaving(true);
          
          // Cancel any pending debounced calls to prevent overlapping saves
          debouncedUpdate.cancel();
          
          // Validate and clean data before sending
          const cleanedData = {
            ...data,
            roles: (data.roles || []).map(role => ({
              ...role,
              // Ensure numeric fields are properly typed
              defaultAllocation: Math.max(0, Math.min(100, Number(role.defaultAllocation) || 100)),
              rate: Math.max(0, Number(role.rate) || 0),
              // Ensure dateRanges are properly structured
              dateRanges: (role.dateRanges || []).map(range => ({
                startMonth: Math.max(1, Number(range.startMonth) || 1),
                endMonth: Math.max(1, Number(range.endMonth) || 1),
                allocation: Math.max(0, Math.min(100, Number(range.allocation) || 0))
              }))
            })),
            fixedCosts: (data.fixedCosts || []).map(cost => ({
              ...cost,
              monthlyCost: Math.max(0, Number(cost.monthlyCost) || 0),
              duration: Math.max(0, Number(cost.duration) || 0),
              startMonth: Math.max(1, Number(cost.startMonth) || 1),
              endMonth: Math.max(1, Number(cost.endMonth) || 1)
            })),
            adjustedMonths: Math.max(1, Number(data.adjustedMonths) || 6)
          };
          
          console.log('CostEstimationWizard: Executing debounced save with cleaned data:', {
            rolesCount: cleanedData.roles?.length || 0,
            fixedCostsCount: cleanedData.fixedCosts?.length || 0
          });
          
          await onUpdate(cleanedData);
          
          // Only update lastSavedDataRef on successful save
          const serializeData = (data) => {
            return JSON.stringify(data, (key, value) => {
              if (typeof value === 'function') return undefined;
              if (value && typeof value === 'object' && !Array.isArray(value)) {
                const sorted = {};
                Object.keys(value).sort().forEach(k => {
                  sorted[k] = value[k];
                });
                return sorted;
              }
              return value;
            });
          };
          lastSavedDataRef.current = serializeData(cleanedData);
          setSaveError(false); // Clear error on successful save
          setLastSaveTime(Date.now()); // Track when we last saved
          console.log('CostEstimationWizard: Debounced save completed successfully');
        } catch (error) {
          console.error('CostEstimationWizard: Auto-save failed:', error);
          console.error('CostEstimationWizard: Error details:', {
            message: error.message,
            stack: error.stack,
            response: error.response
          });
          setSaveError(true);
          // Don't update lastSavedDataRef on failed save to prevent loops
        } finally {
          setSaving(false);
        }
      }
    }, 2000), // Increased debounce delay to 2 seconds
    [onUpdate, readOnly, autoSave, saving, autoSaveDisabled]
  );

  // Core save function
  const saveData = async (data) => {
    if (readOnly || !onUpdate) {
      return;
    }
    
    // Don't save if there are validation errors
    if (hasValidationErrors()) {
      console.log('CostEstimationWizard: Skipping save due to validation errors');
      return;
    }
    
    // Validate data before saving
    if (!data || typeof data !== 'object') {
      console.error('CostEstimationWizard: Invalid data object for saving');
      return;
    }
    
    try {
      console.log('CostEstimationWizard: Saving data with roles:', JSON.stringify(data.roles, null, 2));
      
            // Ensure all roles have proper structure and update legacy dateRanges
              const dataWithCleanedRoles = {
        ...data,
        roles: (data.roles || []).map(role => {
          // Validate role data
          if (!role || typeof role !== 'object') {
            console.warn('CostEstimationWizard: Invalid role object found, skipping');
            return null;
          }
          
          const cleanedRole = {
            ...role,
            id: role.id || `${role.name}-${Date.now()}-${Math.random()}`,
            roster_member_id: role.roster_member_id ?? "",
            name: String(role.name || ''),
            company: String(role.company || "CA"),
            location: String(role.location || "US"),
            function: String(role.function || roleToFunctionMap[role.name] || "Other"),
            focus: role.function === "Developer" ? String(role.focus || roleToFocusMap[role.name] || "Fullstack") : undefined,
            role: String(role.role || ""),
            rate: Math.max(0, Math.round(Number(role.rate) || 0)),
            notes: String(role.notes || ""),
            useDefaultTimeline: role.useDefaultTimeline !== false,
            defaultAllocation: Math.max(0, Math.min(100, Number(role.defaultAllocation) || 100)),
            customDateRanges: Array.isArray(role.customDateRanges) ? role.customDateRanges : [],
            // Update legacy dateRanges for backward compatibility
            dateRanges: getEffectiveDateRanges(role, 1, data.adjustedMonths)
          };
          
          return cleanedRole;
        }).filter(Boolean), // Remove any null roles
        fixedCosts: Array.isArray(data.fixedCosts) ? data.fixedCosts : []
      };
      
            const monthlyBreakdown = calculateMonthlyBreakdown(dataWithCleanedRoles, dataWithCleanedRoles.fixedCosts);
      const baseRoleCost = calculateTotalCurrentRoleCosts(dataWithCleanedRoles);
      const totalFixedCost = (dataWithCleanedRoles.fixedCosts || []).reduce((sum, cost) => {
        const monthlyCost = Number(cost.monthlyCost) || 0;
        const duration = Math.max(0, Number(cost.duration) || dataWithCleanedRoles.adjustedMonths);
        const fixedCost = monthlyCost * duration;
        
        if (isNaN(fixedCost) || fixedCost < 0) {
          console.warn('CostEstimationWizard: Invalid fixed cost calculation:', { monthlyCost, duration, fixedCost });
          return sum;
        }
        
        return sum + fixedCost;
      }, 0);
      
      // Validate cost calculations
      if (isNaN(baseRoleCost) || baseRoleCost < 0) {
        console.error('CostEstimationWizard: Invalid base role cost calculation:', baseRoleCost);
        throw new Error('Invalid role cost calculation');
      }
      
      if (isNaN(totalFixedCost) || totalFixedCost < 0) {
        console.error('CostEstimationWizard: Invalid fixed cost calculation:', totalFixedCost);
        throw new Error('Invalid fixed cost calculation');
      }
      
      // Calculate CAPEX/OPEX amounts
      const totalCost = baseRoleCost + totalFixedCost;
      let capexAmount, opexAmount;
      
      if (totalCost < CAPEX_THRESHOLD && !dataWithCleanedRoles.capexOverride) {
        capexAmount = 0;
        opexAmount = totalCost;
      } else {
        const capexRatio = Math.max(0, Math.min(100, Number(dataWithCleanedRoles.capexRatio) || 0));
        capexAmount = Math.round(baseRoleCost * (capexRatio / 100));
        opexAmount = Math.round(baseRoleCost * ((100 - capexRatio) / 100) + totalFixedCost);
      }
      
            console.log('CostEstimationWizard: Calculated costs:', {
        totalCost,
        baseRoleCost,
        totalFixedCost,
        capexAmount,
        opexAmount
      });
      
      // Validate final calculations
      if (isNaN(capexAmount) || isNaN(opexAmount) || capexAmount < 0 || opexAmount < 0) {
        console.error('CostEstimationWizard: Invalid CAPEX/OPEX calculations:', { capexAmount, opexAmount });
        throw new Error('Invalid CAPEX/OPEX calculation');
      }
      
      const finalData = {
        ...dataWithCleanedRoles,
        capexAmount: Math.round(capexAmount),
        opexAmount: Math.round(opexAmount),
        summary: {
          totalCost: Math.round(totalCost),
          baseRoleCost: Math.round(baseRoleCost),
          totalFixedCost: Math.round(totalFixedCost),
          capexAmount: Math.round(capexAmount),
          opexAmount: Math.round(opexAmount),
          capexRatio: Number(dataWithCleanedRoles.capexRatio) || 0,
          monthlyBreakdown
        }
      };
      
      console.log('CostEstimationWizard: Final data being saved:', JSON.stringify(finalData.roles, null, 2));
      
            // Call onUpdate and wait for it to complete
      await onUpdate(finalData);
      console.log('CostEstimationWizard: Successfully saved data');
    } catch (error) {
      console.error('CostEstimationWizard: Error saving data:', error);
      throw error;
    }
  };

    // Manual save function for critical changes
  const handleManualSave = async () => {
    if (readOnly || !onUpdate || saving) return;
    
    try {
      setSaving(true);
      console.log('CostEstimationWizard: Starting manual save...');
      
      // Cancel any pending debounced saves
      debouncedUpdate.cancel();
      
      // Validate data before saving
      if (!formData || typeof formData !== 'object') {
        console.error('CostEstimationWizard: Invalid form data for saving');
        return;
      }
      
      // Validate and clean data with same logic as auto-save
      const validatedData = {
        ...formData,
        roles: (formData.roles || []).map(role => ({
          ...role,
          // Ensure numeric fields are properly typed and within bounds
          defaultAllocation: Math.max(0, Math.min(100, Number(role.defaultAllocation) || 100)),
          rate: Math.max(0, Number(role.rate) || 0),
          // Ensure dateRanges are properly structured
          dateRanges: (role.dateRanges || []).map(range => ({
            startMonth: Math.max(1, Number(range.startMonth) || 1),
            endMonth: Math.max(1, Number(range.endMonth) || 1),
            allocation: Math.max(0, Math.min(100, Number(range.allocation) || 0))
          })),
          // Ensure required string fields exist
          name: role.name || 'Unknown',
          company: role.company || 'CA',
          location: role.location || 'US',
          function: role.function || 'Other'
        })),
        fixedCosts: (formData.fixedCosts || []).map(cost => ({
          ...cost,
          monthlyCost: Math.max(0, Number(cost.monthlyCost) || 0),
          duration: Math.max(0, Number(cost.duration) || 0),
          startMonth: Math.max(1, Number(cost.startMonth) || 1),
          endMonth: Math.max(1, Number(cost.endMonth) || 1),
          type: cost.type || 'Other'
        })),
        adjustedMonths: Math.max(1, Number(formData.adjustedMonths) || 6)
      };
      
      // Trigger immediate save
      await saveData(validatedData);
      
      const serializeData = (data) => {
        return JSON.stringify(data, (key, value) => {
          if (typeof value === 'function') return undefined;
          // Ignore timestamp fields that change on every save
          if (key === 'last_updated' || key === 'created_date') return undefined;
          if (value && typeof value === 'object' && !Array.isArray(value)) {
            const sorted = {};
            Object.keys(value).sort().forEach(k => {
              // Skip timestamp fields in nested objects too
              if (k !== 'last_updated' && k !== 'created_date') {
                sorted[k] = value[k];
              }
            });
            return sorted;
          }
          return value;
        });
      };
      lastSavedDataRef.current = serializeData(validatedData);
      setSaveError(false); // Clear any previous errors
      setLastSaveTime(Date.now()); // Track when we last saved
      console.log('CostEstimationWizard: Manual save completed successfully');
    } catch (error) {
      console.error('CostEstimationWizard: Manual save failed:', error);
      setSaveError(true);
    } finally {
      setSaving(false);
    }
  };

    // Remove the old debouncedUpdate ref since we now use the memoized version above
  
        useEffect(() => {
    const loadData = async () => {
      try {
                        // Fetch IT roster data
        try {
          console.log('CostEstimationWizard: Using IT roster endpoint...');
          const startTime = performance.now();
          const rosterResponse = await api.getITRoster();
          const endTime = performance.now();
          console.log(`CostEstimationWizard: IT roster loaded in ${(endTime - startTime).toFixed(2)}ms`);
          const rosterData = rosterResponse.data || rosterResponse || [];
          // Ensure we have valid member data
          const validMembers = Array.isArray(rosterData) ? rosterData.filter(member => 
            member && member.id && (member.name || member.first_name || member.last_name)
          ) : [];
          setItRoster(validMembers);
          console.log('Successfully fetched IT roster from API:', validMembers.length, 'valid members out of', rosterData.length, 'total');
        } catch (error) {
          console.warn('API not available for IT roster, using local data:', error);
          const localData = itRosterData || [];
          const validLocalMembers = Array.isArray(localData) ? localData.filter(member => 
            member && member.id && (member.name || member.first_name || member.last_name)
          ) : [];
          setItRoster(validLocalMembers);
          console.log('Using local IT roster data:', validLocalMembers.length, 'valid members');
        }
        
                        // Fetch rates data
        console.log('CostEstimationWizard: Fetching rates data...');
        const ratesStartTime = performance.now();
        const ratesResponse = await api.getRates();
        const ratesEndTime = performance.now();
        console.log(`CostEstimationWizard: Rates data loaded in ${(ratesEndTime - ratesStartTime).toFixed(2)}ms`);
        const rates = ratesResponse.data?.rates || ratesResponse.rates || [];
        
        // Convert daily rates to monthly rates where needed
        const processedRates = rates.map(rate => {
          if (rate.annualWorkingDays !== 1) {
            // If this is a daily rate, convert to monthly
            return {
              ...rate,
              dailyRate: rate.dailyRate,
              monthlyRate: convertDailyToMonthly(rate.dailyRate)
            };
          }
          return rate;
        });
        
        setRateData(processedRates);
        
                // Helper function to get display name for location code
        const getLocationDisplayName = (locationCode) => {
          const locationMap = {
            'US': 'US',
            'UK': 'UK', 
            'CR': 'Costa Rica',
            'BR': 'Brazil',
            'CO': 'Colombia',
            'IND': 'India',
            'MX': 'Mexico'
          };
          return locationMap[locationCode] || locationCode;
        };

        // Extract unique companies
        const companies = [...new Set(processedRates.map(rate => rate.company))].sort();
        if (companies.length > 0) {
          // Create location options by company
          const locationsByCompany = {};
          const rolesByCompanyLocation = {};
          
          companies.forEach(company => {
            // Get all locations for this company
            const companyRates = processedRates.filter(rate => rate.company === company);
            const locationCodes = [...new Set(companyRates.map(rate => rate.location))].sort();
            // Store location objects with code and display name
            locationsByCompany[company] = locationCodes.map(code => ({
              code: code,
              display: getLocationDisplayName(code)
            }));
            
            // Get all roles for each company+location combination
            rolesByCompanyLocation[company] = {};
            locationCodes.forEach(locationCode => {
              const locationRates = companyRates.filter(rate => rate.location === locationCode);
              const roles = [...new Set(locationRates.map(rate => rate.role))].sort();
              rolesByCompanyLocation[company][locationCode] = roles;
            });
          });
          
          setLocationOptions(locationsByCompany);
          setRoleOptions(rolesByCompanyLocation);
        }
        
        setDataLoaded(true);
      } catch (err) {
        console.error("Failed to fetch data:", err);
        setDataLoaded(true); // Still mark as loaded to show the component
      }
    };
    
    loadData();
  }, []);

    // Apply COLA adjustments to existing roles when data is loaded
  useEffect(() => {
    if (formData.roles && formData.roles.length > 0 && rateData.length > 0 && dataLoaded) {
      const projectYear = getProjectYear(outcomeData, formData, new Date().getFullYear());
      
      console.log('CostEstimationWizard: Checking for COLA adjustments...');
      console.log('Project year:', projectYear);
      console.log('Outcome data:', outcomeData);
      console.log('Form data start_month:', formData.start_month);
      
      // Log all roles for debugging
      formData.roles.forEach(role => {
        console.log(`Role ${role.name}:`, {
          id: role.id,
          company: role.company,
          location: role.location,
          role: role.role,
          rate: role.rate,
          roster_member_id: role.roster_member_id,
          rateAdjustmentInfo: role.rateAdjustmentInfo
        });
      });
      
                  // Check if any roles need COLA adjustment
      const rolesNeedingUpdate = formData.roles.filter(role => {
        // Apply COLA to roles that:
        // 1. Haven't been COLA adjusted yet
        // 2. Have company/location info
        // 3. Have a rate to adjust
        const needsCOLA = !role.rateAdjustmentInfo && // Not already adjusted
                         role.company && role.location && // Has company/location info
                         role.rate > 0; // Has a rate to adjust
        
        return needsCOLA;
      });
      
      console.log(`CostEstimationWizard: Found ${rolesNeedingUpdate.length} roles needing COLA check`);
      
      if (rolesNeedingUpdate.length > 0) {
        console.log('CostEstimationWizard: Applying COLA adjustments to existing roles for project year:', projectYear);
        
        const updatePromises = rolesNeedingUpdate.map(async role => {
          try {
            console.log(`Checking COLA for role ${role.name} (${role.company}/${role.location}/${role.role})`);
            const rateInfo = await getRateForRole(role.company, role.location, role.role || '', projectYear);
            console.log(`Rate info result:`, rateInfo);
            
            if (rateInfo.monthlyRate > 0) {
              const rateDifference = Math.abs(rateInfo.monthlyRate - role.rate);
              const isAdjusted = rateInfo.isAdjusted || rateDifference > 1;
              console.log(`CostEstimationWizard: Rate analysis for role ${role.name}:`, {
                currentRate: role.rate,
                newRate: rateInfo.monthlyRate,
                difference: rateDifference,
                isAdjusted: isAdjusted,
                rateInfoIsAdjusted: rateInfo.isAdjusted,
                adjustmentInfo: rateInfo.adjustmentInfo
              });
              
              if (isAdjusted) {
                return {
                  roleId: role.id,
                  newRate: rateInfo.monthlyRate,
                  rateAdjustmentInfo: rateInfo.adjustmentInfo
                };
              }
            }
          } catch (error) {
            console.warn(`Could not get COLA rate for role ${role.id}:`, error);
          }
          return null;
        });
        
        Promise.all(updatePromises).then(updates => {
          const validUpdates = updates.filter(Boolean);
          console.log(`CostEstimationWizard: Valid updates found:`, validUpdates);
          
          if (validUpdates.length > 0) {
            console.log(`CostEstimationWizard: Applying COLA adjustments to ${validUpdates.length} roles`);
            
            setFormData(prev => ({
              ...prev,
              roles: ensureRoleFields((prev.roles || []).map(role => {
                const update = validUpdates.find(u => u.roleId === role.id);
                if (update) {
                  console.log(`Updating role ${role.name} with COLA adjustment:`, update);
                  return {
                    ...role,
                    rate: update.newRate,
                    rateAdjustmentInfo: update.rateAdjustmentInfo
                  };
                }
                return role;
              }), prev.adjustedMonths)
            }));
          } else {
            console.log('CostEstimationWizard: No COLA adjustments needed');
          }
        });
      } else {
        console.log('CostEstimationWizard: No roles need COLA adjustment');
      }
    }
  }, [formData.roles, rateData, outcomeData, formData.start_month, dataLoaded]);

  useEffect(() => {
    if (outcomeStartDate) {
      const newStartMonth = dayjs(outcomeStartDate).format('YYYY-MM');
      if (newStartMonth !== formData.start_month) {
        setFormData(prev => ({
          ...prev,
          start_month: newStartMonth
        }));
      }
    }
  }, [outcomeStartDate, formData.start_month]);

  useEffect(() => {
    if (outcomeName && outcomeName !== formData.outcome_name) {
      setFormData(prev => ({
        ...prev,
        outcome_name: outcomeName
      }));
    }
  }, [outcomeName, formData.outcome_name]);

  // Generate team recommendation when useTeamRecommendation is enabled and no roles exist
  useEffect(() => {
    if ((!data || !data.roles || data.roles.length === 0) && !formData.isCustomized && dataLoaded && formData.useTeamRecommendation) {
      const baseMonths = sizeMonths[formData.size] || 6;
      const confidenceMultiplier = confidenceMultipliers[formData.confidence] || 1.0;
      const adjustedMonths = Math.round(baseMonths * confidenceMultiplier);

      const roleMatrix = sizeRoleMatrix[formData.size] || {};
      const newRoles = Object.entries(roleMatrix)
        .filter(([, config]) => config.count > 0)
        .flatMap(([roleName, config]) => {
          return Array.from({ length: config.count }, (_, index) => ({
            id: `${roleName}-${Date.now()}-${index}`,
            name: roleName,
            company: "CA",
            location: "US",
            function: roleToFunctionMap[roleName] || "Other",
            focus: roleToFunctionMap[roleName] === "Developer" ? (roleToFocusMap[roleName] || "Fullstack") : undefined,
            role: roleName,
            rate: Math.round(formData.rates["CA"] || Math.round(163000 / 12)),
            roster_member_id: "",
            notes: "",
            useDefaultTimeline: true,
            defaultAllocation: config.percent,
            customDateRanges: [],
            dateRanges: [{ startMonth: 1, endMonth: adjustedMonths, allocation: config.percent }]
          }));
        });

      setFormData(prev => ({
        ...prev,
        months: baseMonths,
        adjustedMonths: adjustedMonths,
        roles: ensureRoleFields(newRoles, adjustedMonths),
        isCustomized: false,
        fixedCosts: (prev.fixedCosts || []).map(cost => ({
          ...cost,
          endMonth: adjustedMonths,
          duration: adjustedMonths
        }))
      }));
    }
  }, [formData.size, formData.confidence, data, formData.rates, formData.isCustomized, dataLoaded, formData.useTeamRecommendation]);

    // Auto-save when formData changes - with improved change detection and debouncing
  useEffect(() => {
    if (!initializedRef.current) {
      // Mark as initialized and store initial data
      initializedRef.current = true;
      const serializeData = (data) => {
        return JSON.stringify(data, (key, value) => {
          if (typeof value === 'function') return undefined;
          // Ignore timestamp fields that change on every save
          if (key === 'last_updated' || key === 'created_date') return undefined;
          if (value && typeof value === 'object' && !Array.isArray(value)) {
            const sorted = {};
            Object.keys(value).sort().forEach(k => {
              // Skip timestamp fields in nested objects too
              if (k !== 'last_updated' && k !== 'created_date') {
                sorted[k] = value[k];
              }
            });
            return sorted;
          }
          return value;
        });
      };
      lastSavedDataRef.current = serializeData(formData);
      console.log('CostEstimationWizard: Initialized with data');
      return;
    }

    // Only auto-save if conditions are met and enough time has passed since last save
    const timeSinceLastSave = Date.now() - lastSaveTime;
    const canAutoSave = autoSave && dataLoaded && !saving && !saveError && !isUpdating && !isRefreshing && !autoSaveDisabled && timeSinceLastSave > 3000;
    
    if (canAutoSave) {
      const timeoutId = setTimeout(() => {
        const currentTimeSinceLastSave = Date.now() - lastSaveTime;
        if (hasDataChanged(formData) && !saving && !isUpdating && !isRefreshing && !autoSaveDisabled && currentTimeSinceLastSave > 3000) {
          console.log('CostEstimationWizard: Data changed, triggering auto-save (time since last save:', currentTimeSinceLastSave, 'ms)');
          debouncedUpdate(formData);
        }
      }, 500); // Increased delay to prevent rapid-fire changes
      
      return () => clearTimeout(timeoutId);
    }
  }, [formData, debouncedUpdate, autoSave, dataLoaded, saving, saveError, isUpdating, isRefreshing, autoSaveDisabled, lastSaveTime, hasDataChanged]);

  useEffect(() => {
    setFormData(prev => ({ ...prev, fixedCosts }));
  }, [fixedCosts]);

  // Apply staggered start when enabled
  useEffect(() => {
    if (formData.staggeredStart && formData.useTeamRecommendation) {
      setFormData(prev => ({
        ...prev,
        roles: ensureRoleFields((prev.roles || []).map(role => {
          if (["FE", "BE", "QA"].includes(role.name) && role.useDefaultTimeline !== false) {
            return {
              ...role,
              useDefaultTimeline: false,
              customDateRanges: [{
                startMonth: 3, // Start 2 months later
                endMonth: formData.adjustedMonths,
                allocation: role.defaultAllocation || 100
              }],
              dateRanges: [{
                startMonth: 3,
                endMonth: formData.adjustedMonths,
                allocation: role.defaultAllocation || 100
              }]
            };
          }
          return role;
        }), formData.adjustedMonths)
      }));
    }
  }, [formData.staggeredStart, formData.adjustedMonths, formData.useTeamRecommendation]);

  // Collapse sections when manual changes are detected
  useEffect(() => {
    if (hasManualRoleChanges()) {
      setTeamRecommendationExpanded(false);
      setStaggeredStartExpanded(false);
    }
  }, [formData.roles, formData.isCustomized, formData.adjustedMonths, formData.rates]);

        // Handle new member added
  const handleMemberAdded = async (newMember) => {
    try {
      // Refresh IT roster data
      try {
        const rosterResponse = await api.getITRoster();
        const rosterData = rosterResponse.data || rosterResponse || [];
        // Ensure we have valid member data
        const validMembers = Array.isArray(rosterData) ? rosterData.filter(member => 
          member && member.id && (member.name || member.first_name || member.last_name)
        ) : [];
        setItRoster(validMembers);
        console.log('Successfully refreshed IT roster from API after adding member:', validMembers.length, 'valid members');
      } catch (error) {
        console.warn('API not available for IT roster refresh, using local data:', error);
        setItRoster(itRosterData || []);
      }
      
      console.log('IT roster refreshed after adding new member');
    } catch (error) {
      console.error('Error refreshing IT roster:', error);
    }
  };

  const handleChange = async (field, value) => {
    if (readOnly) return; // Prevent changes in read-only mode
    
    console.log(`handleChange called: field=${field}, value=${value}`);
    
    setFormData(prev => {
      let updates = { [field]: value };

      if (field === 'useTeamRecommendation') {
        updates.useTeamRecommendation = value;
        if (value) {
          // Re-enable team recommendation mode
          setTeamRecommendationExpanded(true);
          setStaggeredStartExpanded(true);
          updates.isCustomized = false;
        }
        return { ...prev, ...updates };
      }

      if (field === 'size' && formData.useTeamRecommendation) {
        const baseMonths = sizeMonths[value] || 6;
        const confidenceMultiplier = confidenceMultipliers[prev.confidence] || 1.0;
        const adjustedMonths = Math.round(baseMonths * confidenceMultiplier);

        updates.months = baseMonths;
        updates.adjustedMonths = adjustedMonths;
        updates.monthsManuallySet = false;

        const roleMatrix = sizeRoleMatrix[value] || {};
        const newRoles = Object.entries(roleMatrix)
          .filter(([, config]) => config.count > 0)
          .flatMap(([roleName, config]) => {
            return Array.from({ length: config.count }, (_, index) => ({
              id: `${roleName}-${Date.now()}-${index}`,
              name: roleName,
              company: "CA",
              location: "US",
              function: roleToFunctionMap[roleName] || "Other",
              focus: roleToFunctionMap[roleName] === "Developer" ? (roleToFocusMap[roleName] || "Fullstack") : undefined,
              role: roleName,
              rate: Math.round(prev.rates["CA"] || Math.round(163000 / 12)),
              roster_member_id: "",
              notes: "",
              useDefaultTimeline: true,
              defaultAllocation: config.percent,
              customDateRanges: [],
              dateRanges: [{ startMonth: 1, endMonth: adjustedMonths, allocation: config.percent }]
            }));
          });

        updates.roles = ensureRoleFields(newRoles, adjustedMonths);
        updates.isCustomized = false;
        updates.fixedCosts = (prev.fixedCosts || []).map(cost => ({
          ...cost,
          endMonth: adjustedMonths,
          duration: adjustedMonths
        }));
      }

      if (field === 'confidence' && formData.useTeamRecommendation) {
        const baseMonths = prev.months;
        const confidenceMultiplier = confidenceMultipliers[value] || 1.0;
        const adjustedMonths = Math.round(baseMonths * confidenceMultiplier);

        updates.adjustedMonths = adjustedMonths;

        // Update roles using default timeline
        updates.roles = ensureRoleFields((prev.roles || []).map(role => {
          if (role.useDefaultTimeline !== false) {
            return {
              ...role,
              dateRanges: [{ startMonth: 1, endMonth: adjustedMonths, allocation: role.defaultAllocation || 100 }]
            };
          }
          return role;
        }), adjustedMonths);

        updates.fixedCosts = (prev.fixedCosts || []).map(cost => ({
          ...cost,
          endMonth: adjustedMonths,
          duration: adjustedMonths
        }));
      }

      // Handle timeline changes from header controls
      if (field === 'start_month') {
        updates.start_month = value;
        return { ...prev, ...updates };
      }

      if (field === 'adjustedMonths') {
        const newMonths = Number(value);
        const oldMonths = Number(prev.adjustedMonths);
        const oldStartMonth = prev.start_month;
        const newStartMonth = prev.start_month; // Start month doesn't change in this case
        
        console.log(`Timeline change detected: ${oldMonths} -> ${newMonths}, roles count: ${(prev.roles || []).length}`);
        
        // Show modal if there are existing roles and the timeline is changing
        if (prev.roles && prev.roles.length > 0 && newMonths !== oldMonths) {
          console.log('Showing timeline adjustment modal - roles exist and timeline changed');
          setPendingTimelineChange({ 
            field, 
            value: newMonths, 
            oldMonths, 
            newMonths,
            oldStartMonth,
            newStartMonth
          });
          setTimelineModalOpen(true);
          return prev; // Don't update yet, wait for user choice
        }
        
        console.log('No roles exist or no change, updating directly');
        
        // If no roles exist, just update directly
        updates.monthsManuallySet = true;
        updates.adjustedMonths = newMonths;

        // Auto-adjust fixed costs
        updates.fixedCosts = (prev.fixedCosts || []).map(cost => ({
          ...cost,
          endMonth: Math.min(cost.endMonth || oldMonths, newMonths),
          duration: Math.max(0, Math.min(cost.endMonth || oldMonths, newMonths) - (cost.startMonth || 1) + 1)
        }));
        
        // Update the fixedCosts state as well
        setFixedCosts(updates.fixedCosts);
      }

      if (field === 'capexRatio') {
        updates.capexRatio = Math.max(0, Math.min(100, value));
      }

      if (field === 'capexOverride') {
        updates.capexOverride = value;
        // If turning off override for under-threshold projects, set to 0% CAPEX (100% OPEX)
        if (!value && isUnderCapexThreshold()) {
          updates.capexRatio = 0;
        }
      }

      if (
        field === 'size' ||
        field === 'confidence' ||
        field === 'adjustedMonths' ||
        field === 'capexRatio' ||
        field === 'capexOverride'
      ) {
        // These fields don't mark as customized
      } else {
        updates.isCustomized = true;
      }

      const newData = { ...prev, ...updates, roles: ensureRoleFields((updates.roles || prev.roles), prev.adjustedMonths) };
      
      return newData;
    });
  };

  // Handle header date changes
  const handleHeaderDateChange = (field, value) => {
    if (readOnly) return;
    
    console.log(`Header date change: ${field} = ${value}`);
    
    setFormData(prev => {
      const oldStartMonth = prev.start_month;
      const oldMonths = prev.adjustedMonths;
      
      let newStartMonth = oldStartMonth;
      let newMonths = oldMonths;
      
      if (field === 'start_month') {
        newStartMonth = value;
      } else if (field === 'end_month') {
        // Calculate new duration based on end month
        const startDate = dayjs(prev.start_month + '-01');
        const endDate = dayjs(value + '-01');
        newMonths = endDate.diff(startDate, 'month') + 1;
      }
      
      // Check if we need to show the timeline modal
      const hasRoles = prev.roles && prev.roles.length > 0;
      const timelineChanged = newStartMonth !== oldStartMonth || newMonths !== oldMonths;
      
      if (hasRoles && timelineChanged) {
        console.log('Showing timeline adjustment modal for header date change');
        setPendingTimelineChange({
          field: field === 'start_month' ? 'start_month' : 'adjustedMonths',
          value: field === 'start_month' ? newStartMonth : newMonths,
          oldMonths,
          newMonths,
          oldStartMonth,
          newStartMonth
        });
        setTimelineModalOpen(true);
        return prev; // Don't update yet, wait for user choice
      }
      
      // No roles or no change, update directly
      const updates = {
        start_month: newStartMonth,
        adjustedMonths: newMonths,
        monthsManuallySet: true
      };
      
      // Auto-adjust fixed costs
      updates.fixedCosts = (prev.fixedCosts || []).map(cost => ({
        ...cost,
        endMonth: Math.min(cost.endMonth || oldMonths, newMonths),
        duration: Math.max(0, Math.min(cost.endMonth || oldMonths, newMonths) - (cost.startMonth || 1) + 1)
      }));
      
      setFixedCosts(updates.fixedCosts);
      
      return { ...prev, ...updates };
    });
  };

  const handleTimelineAdjustmentConfirm = (choice) => {
    if (!pendingTimelineChange) return;
    
    const { field, value, oldMonths, newMonths, oldStartMonth, newStartMonth } = pendingTimelineChange;
    
    console.log(`Timeline adjustment: ${choice}, field: ${field}, value: ${value}`);
    
    setFormData(prev => {
      let updates = {
        monthsManuallySet: true
      };
      
      // Apply the pending change
      if (field === 'start_month') {
        updates.start_month = value;
        updates.adjustedMonths = newMonths;
      } else if (field === 'adjustedMonths') {
        updates.adjustedMonths = value;
        updates.start_month = newStartMonth;
      }

      if (choice === 'auto-adjust') {
        console.log('Auto-adjusting role timelines...');
        
        updates.roles = (prev.roles || []).map(role => {
          if (role.useDefaultTimeline !== false) {
            // Update roles using default timeline
            return {
              ...role,
              dateRanges: [{ startMonth: 1, endMonth: newMonths, allocation: role.defaultAllocation || 100 }]
            };
          } else {
            // Keep custom timelines as-is but update legacy dateRanges
            return {
              ...role,
              dateRanges: role.customDateRanges || role.dateRanges
            };
          }
        });
        
        console.log('Roles after adjustment:', JSON.stringify(updates.roles, null, 2));
      } else {
        updates.roles = prev.roles;
      }

      // Update fixed costs
      updates.fixedCosts = (prev.fixedCosts || []).map(cost => ({
        ...cost,
        endMonth: Math.min(cost.endMonth || oldMonths, newMonths),
        duration: Math.max(0, Math.min(cost.endMonth || oldMonths, newMonths) - (cost.startMonth || 1) + 1)
      }));

      setFixedCosts(updates.fixedCosts);

      const newData = { ...prev, ...updates };
      
      return newData;
    });
    
    setPendingTimelineChange(null);
  };

  const handleAddRole = (roleName) => {
    if (readOnly) return; // Prevent changes in read-only mode
    
    setFormData(prev => {
            // Find the first available location for the selected company (default to CA)
      const company = "CA";
      const availableLocations = locationOptions[company] || [];
      const location = availableLocations.length > 0 ? availableLocations[0].code : "US";
      
      // Find the first available role for the selected company and location
      const availableRoles = (roleOptions[company] && roleOptions[company][location]) ? roleOptions[company][location] : [];
      const mappedFunction = roleToFunctionMap[roleName] || "Other";
      const mappedFocus = mappedFunction === "Developer" ? (roleToFocusMap[roleName] || "Fullstack") : undefined;
      const roleName_specific = availableRoles.find(r => r.toLowerCase().includes(roleName.toLowerCase())) || "";
      
      // Find the appropriate rate for this company/location/role
      let rate = Math.round(prev.rates[company] || Math.round(163000 / 12));
      const rateInfo = rateData.find(r => 
        r.company === company && 
        r.location === location && 
        r.role === roleName_specific
      );
      
      if (rateInfo) {
        if (rateInfo.annualWorkingDays === 1) {
          // Annual rate
          rate = Math.round(rateInfo.dailyRate / 12);
        } else {
          // Daily rate converted to monthly
          rate = convertDailyToMonthly(rateInfo.dailyRate);
        }
      }
      
      const newData = {
        ...prev,
        isCustomized: true,
        roles: ensureRoleFields([
          ...(prev.roles || []),
          {
            id: `${roleName}-${Date.now()}`,
            name: roleName,
            company: company,
            location: location,
            function: mappedFunction,
            focus: mappedFocus,
            role: roleName_specific,
            rate: rate,
            roster_member_id: "",
            notes: "",
            useDefaultTimeline: true,
            defaultAllocation: 100,
            customDateRanges: [],
            dateRanges: [{ startMonth: 1, endMonth: prev.adjustedMonths, allocation: 100 }]
          }
        ], prev.adjustedMonths)
      };
      
      return newData;
    });
  };

    const handleRemoveRole = (roleId) => {
    if (readOnly) return; // Prevent changes in read-only mode
    
    setFormData(prev => {
      const newData = {
        ...prev,
        isCustomized: true,
        roles: ensureRoleFields((prev.roles || []).filter(role => role.id !== roleId), prev.adjustedMonths)
      };
      
      return newData;
    });
  };

  // Handle role reordering
  const handleMoveRole = (roleId, direction) => {
    if (readOnly || saving) return;
    
    setIsUpdating(true);
    
    setFormData(prev => {
      const roles = prev.roles || [];
      const currentIndex = roles.findIndex(role => role.id === roleId);
      
      if (currentIndex === -1) return prev;
      
      const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
      
      if (newIndex >= 0 && newIndex < roles.length) {
        const newRoles = [...roles];
        // Swap elements
        [newRoles[currentIndex], newRoles[newIndex]] = [newRoles[newIndex], newRoles[currentIndex]];
        
        return {
          ...prev,
          isCustomized: true,
          roles: ensureRoleFields(newRoles, prev.adjustedMonths)
        };
      }
      
      return prev;
    });
    
    setTimeout(() => setIsUpdating(false), 500);
  };

          // Handle role changes with debouncing to prevent loops
  const handleRoleChange = useCallback(async (roleId, field, value) => {
    if (readOnly || saving) return; // Prevent changes during save
    
    console.log(`CostEstimationWizard: Changing role ${roleId} field ${field} to ${value}`);
    
    setIsUpdating(true); // Mark as updating to prevent auto-save
    
    setFormData(prev => {
      const updatedRoles = (prev.roles || []).map(role => {
        if (role.id === roleId) {
          let updated = { ...role, [field]: value };
          
                                        // Handle allocation changes - update both defaultAllocation and dateRanges
          if (field === 'allocation') {
            const allocationValue = Math.max(0, Math.min(100, Number(value) || 0));
            updated.defaultAllocation = allocationValue;
            
            // If using default timeline, update dateRanges
            if (updated.useDefaultTimeline !== false) {
              updated.dateRanges = [{
                startMonth: 1,
                endMonth: prev.adjustedMonths,
                allocation: allocationValue
              }];
            } else {
              // For custom timelines, update all custom date ranges with the new allocation
              if (updated.customDateRanges && updated.customDateRanges.length > 0) {
                updated.customDateRanges = updated.customDateRanges.map(range => ({
                  ...range,
                  allocation: allocationValue
                }));
                updated.dateRanges = [...updated.customDateRanges];
              }
            }
            
            console.log(`CostEstimationWizard: Updated allocation for role ${role.name} to ${allocationValue}%`);
            console.log(`CostEstimationWizard: Updated dateRanges:`, updated.dateRanges);
          }
          
          // If company changes, reset location and role
          if (field === 'company') {
            const availableLocations = locationOptions[value] || [];
            updated.location = availableLocations.length > 0 ? availableLocations[0].code : '';
            updated.role = '';
          }
          
          // If location changes, reset role
          if (field === 'location') {
            updated.role = '';
          }
          
                                                              // If roster_member_id changes, update all metadata fields
                if (field === 'roster_member_id') {
                  if (value && value !== '') {
                    // Assigning a roster member - use their data
                    const member = itRoster.find(m => m.id === value);
                    if (member) {
                      // Calculate salary dynamically for the member
                      calculateMemberSalary(member, getProjectYear(outcomeData, formData, new Date().getFullYear()))
                        .then(memberSalaryInfo => {
                          setFormData(current => ({
                            ...current,
                            roles: ensureRoleFields(current.roles.map(r => {
                              if (r.id === roleId) {
                                return {
                                  ...r,
                                  rate: memberSalaryInfo.monthlySalary || 0,
                                                            company: member.company || "CA",
                          location: member.location || "US",
                          function: member.function || roleToFunctionMap[updated.name] || "Other",
                          focus: member.function === "Developer" ? member.focus : undefined,
                          role: member.role || updated.name,
                                  rateAdjustmentInfo: memberSalaryInfo.isAdjusted ? memberSalaryInfo.adjustmentInfo : null
                                };
                              }
                              return r;
                            }), current.adjustedMonths)
                          }));
                        })
                        .catch(error => {
                          console.warn('Could not calculate member salary:', error);
                        });
                      
                                              updated = {
                          ...updated,
                          company: member.company || "CA",
                          location: member.location || "US",
                          function: member.function || roleToFunctionMap[updated.name] || "Other",
                          focus: member.function === "Developer" ? member.focus : undefined,
                          role: member.role || updated.name
                        };
                
                // Check if roster member's rate needs COLA adjustment
                const projectYear = getProjectYear(outcomeData, formData, new Date().getFullYear());
                
                getRateForRole(
                  updated.company, 
                  updated.location, 
                  updated.role || '', 
                  projectYear
                ).then(rateInfo => {
                  if (rateInfo.isAdjusted && rateInfo.monthlyRate > 0) {
                    // Apply COLA adjustment to roster member rate
                    setFormData(current => ({
                      ...current,
                      roles: ensureRoleFields(current.roles.map(r => {
                        if (r.id === roleId) {
                          return {
                            ...r,
                            rate: rateInfo.monthlyRate,
                            rateAdjustmentInfo: rateInfo.adjustmentInfo
                          };
                        }
                        return r;
                      }), current.adjustedMonths)
                    }));
                  }
                }).catch(error => {
                  console.warn('Could not check COLA adjustment for roster member:', error);
                });
              }
            } else {
              // Unassigning roster member - restore COLA-adjusted rate
              const projectYear = getProjectYear(outcomeData, formData, new Date().getFullYear());
              
              // Get COLA-adjusted rate asynchronously
              getRateForRole(
                updated.company, 
                updated.location, 
                updated.role || '', 
                projectYear
              ).then(rateInfo => {
                if (rateInfo.monthlyRate > 0) {
                  setFormData(current => ({
                    ...current,
                    roles: ensureRoleFields(current.roles.map(r => {
                      if (r.id === roleId) {
                        return {
                          ...r,
                          rate: rateInfo.monthlyRate,
                          rateAdjustmentInfo: rateInfo.isAdjusted ? rateInfo.adjustmentInfo : null
                        };
                      }
                      return r;
                    }), current.adjustedMonths)
                  }));
                }
              }).catch(error => {
                console.warn('Could not restore COLA-adjusted rate:', error);
              });
            }
          }
          
          return updated;
        }
        return role;
      });
      
                  const newData = {
        ...prev,
        isCustomized: true,
        roles: ensureRoleFields(updatedRoles, prev.adjustedMonths)
      };
      
      // Handle COLA rate updates asynchronously for non-roster roles
      if (['company', 'location', 'role'].includes(field)) {
        const targetRole = updatedRoles.find(r => r.id === roleId);
        if (targetRole && !targetRole.roster_member_id) {
          const projectYear = getProjectYear(outcomeData, formData, new Date().getFullYear());
          
          getRateForRole(
            targetRole.company, 
            targetRole.location, 
            targetRole.role || '', 
            projectYear
          ).then(rateInfo => {
            if (rateInfo.monthlyRate > 0) {
              setFormData(current => ({
                ...current,
                roles: ensureRoleFields(current.roles.map(r => {
                  if (r.id === roleId) {
                    return {
                      ...r,
                      rate: rateInfo.monthlyRate,
                      rateAdjustmentInfo: rateInfo.isAdjusted ? rateInfo.adjustmentInfo : null
                    };
                  }
                  return r;
                }), current.adjustedMonths)
              }));
            }
          }).catch(error => {
            console.warn('Could not get COLA-adjusted rate:', error);
          });
        }
      }
      
            // Validate allocation values to ensure they're within bounds
      if (field === 'allocation') {
        const allocationValue = Number(value) || 0;
        if (allocationValue < 0 || allocationValue > 100) {
          const targetRole = updatedRoles.find(r => r.id === roleId);
          console.warn(`CostEstimationWizard: Invalid allocation value ${allocationValue}% for role ${targetRole?.name || 'unknown'}`);
        }
      }
      
      return newData;
    });
    
    // Clear updating flag after a short delay
    setTimeout(() => setIsUpdating(false), 500);
    
    // Note: Removed force save to prevent loops - auto-save will handle this
  }, [readOnly, saving, formData.adjustedMonths, outcomeData, formData, itRoster, locationOptions]);

  // Handle opening add role dialog
  const handleOpenAddRoleDialog = async () => {
    // Reset form with defaults and get COLA-adjusted rate
    const projectYear = getProjectYear(outcomeData, formData, new Date().getFullYear());
    
    try {
      const rateInfo = await getRateForRole('CA', 'US', '', projectYear);
      setNewRoleForm({
        name: 'Product',
        company: 'CA',
        location: 'US',
        function: 'Product Manager',
        role: '',
        rate: rateInfo.monthlyRate || 13583,
        rateAdjustmentInfo: rateInfo.isAdjusted ? rateInfo.adjustmentInfo : null
      });
    } catch (error) {
      console.warn('Could not get COLA-adjusted rate for new role:', error);
      setNewRoleForm({
        name: 'Product',
        company: 'CA',
        location: 'US',
        function: 'Product Manager',
        role: '',
        rate: 13583,
        rateAdjustmentInfo: null
      });
    }
    
    setAddRoleDialogOpen(true);
  };

  // Handle opening edit role dialog
  const handleOpenEditRoleDialog = (role) => {
    setEditingRoleMetadata(role);
    setEditRoleForm({
      name: role.name,
      company: role.company,
      location: role.location,
      function: role.function,
      role: role.role,
      rate: role.rate,
      rateAdjustmentInfo: role.rateAdjustmentInfo || null
    });
    setEditRoleDialogOpen(true);
  };

  // Handle add role form changes
  const handleNewRoleFormChange = async (field, value) => {
    setNewRoleForm(prev => {
      let updated = { ...prev, [field]: value };
      
      // Update dependent fields and rates
      if (field === 'name') {
        updated.function = roleToFunctionMap[value] || "Other";
      }
      
            if (field === 'company') {
        const availableLocations = locationOptions[value] || [];
        updated.location = availableLocations.length > 0 ? availableLocations[0].code : 'US';
        updated.role = '';
      }
      
      if (field === 'location') {
        updated.role = '';
      }
      
      // Update rate when company, location, or role changes
      if (['company', 'location', 'role'].includes(field)) {
        const projectYear = getProjectYear(outcomeData, formData, new Date().getFullYear());
        
        // Get COLA-adjusted rate asynchronously
        getRateForRole(
          updated.company, 
          updated.location, 
          field === 'role' ? value : updated.role, 
          projectYear
        ).then(rateInfo => {
          if (rateInfo.monthlyRate > 0) {
            setNewRoleForm(current => ({
              ...current,
              rate: rateInfo.monthlyRate,
              rateAdjustmentInfo: rateInfo.isAdjusted ? rateInfo.adjustmentInfo : null
            }));
          }
        }).catch(error => {
          console.warn('Could not get COLA-adjusted rate:', error);
        });
      }
      
      return updated;
    });
  };

  // Handle edit role form changes
  const handleEditRoleFormChange = async (field, value) => {
    setEditRoleForm(prev => {
      let updated = { ...prev, [field]: value };
      
      // Update dependent fields and rates
      if (field === 'name') {
        updated.function = roleToFunctionMap[value] || "Other";
      }
      
            if (field === 'company') {
        const availableLocations = locationOptions[value] || [];
        updated.location = availableLocations.length > 0 ? availableLocations[0].code : 'US';
        updated.role = '';
      }
      
      if (field === 'location') {
        updated.role = '';
      }
      
      // Update rate when company, location, or role changes
      if (['company', 'location', 'role'].includes(field)) {
        const projectYear = getProjectYear(outcomeData, formData, new Date().getFullYear());
        
        // Get COLA-adjusted rate asynchronously
        getRateForRole(
          updated.company, 
          updated.location, 
          field === 'role' ? value : updated.role, 
          projectYear
        ).then(rateInfo => {
          if (rateInfo.monthlyRate > 0) {
            setEditRoleForm(current => ({
              ...current,
              rate: rateInfo.monthlyRate,
              rateAdjustmentInfo: rateInfo.isAdjusted ? rateInfo.adjustmentInfo : null
            }));
          }
        }).catch(error => {
          console.warn('Could not get COLA-adjusted rate:', error);
        });
      }
      
      return updated;
    });
  };

  // Handle adding role
  const handleAddRoleFromDialog = () => {
    if (readOnly) return;
    
    const mappedFocus = newRoleForm.function === "Developer" ? (roleToFocusMap[newRoleForm.name] || "Fullstack") : undefined;
    
    const newRole = {
      id: `${newRoleForm.name}-${Date.now()}`,
      name: newRoleForm.name,
      company: newRoleForm.company,
      location: newRoleForm.location,
      function: newRoleForm.function,
      focus: mappedFocus,
      role: newRoleForm.role,
      rate: newRoleForm.rate,
      roster_member_id: "",
      notes: "",
      useDefaultTimeline: true,
      defaultAllocation: 100,
      customDateRanges: [],
      dateRanges: [{
        startMonth: 1,
        endMonth: formData.adjustedMonths,
        allocation: 100
      }],
      rateAdjustmentInfo: newRoleForm.rateAdjustmentInfo
    };
    
    setFormData(prev => ({
      ...prev,
      isCustomized: true,
      roles: ensureRoleFields([...(prev.roles || []), newRole], prev.adjustedMonths)
    }));
    
    setAddRoleDialogOpen(false);
  };

  // Handle saving edited role
  const handleSaveEditedRole = () => {
    if (readOnly || !editingRoleMetadata) return;
    
    setFormData(prev => ({
      ...prev,
      isCustomized: true,
      roles: ensureRoleFields((prev.roles || []).map(role => {
        if (role.id === editingRoleMetadata.id) {
          return {
            ...role,
            name: editRoleForm.name,
            company: editRoleForm.company,
            location: editRoleForm.location,
            function: editRoleForm.function,
            role: editRoleForm.role,
            rate: editRoleForm.rate,
            focus: editRoleForm.function === "Developer" ? (roleToFocusMap[editRoleForm.name] || "Fullstack") : undefined,
            rateAdjustmentInfo: editRoleForm.rateAdjustmentInfo
          };
        }
        return role;
      }), prev.adjustedMonths)
    }));
    
    setEditRoleDialogOpen(false);
    setEditingRoleMetadata(null);
  };

  const handleEditRoleTimeline = (role) => {
    if (readOnly) return;
    setEditingRole(role);
    setRoleTimelineModalOpen(true);
  };

  const handleSaveRoleTimeline = (updatedRole) => {
    setFormData(prev => {
      const newData = {
        ...prev,
        isCustomized: true,
        roles: ensureRoleFields((prev.roles || []).map(role => 
          role.id === updatedRole.id ? updatedRole : role
        ), prev.adjustedMonths)
      };
      
      return newData;
    });
  };

  const handleAddFixedCost = () => {
    if (readOnly) return; // Prevent changes in read-only mode
    
    const newCost = {
      id: Date.now(),
      type: 'Licenses',
      monthlyCost: 0,
      details: '',
      startMonth: 1,
      endMonth: formData.adjustedMonths,
      duration: formData.adjustedMonths
    };
    setFixedCosts(prev => [...prev, newCost]);
  };

  const handleRemoveFixedCost = (costId) => {
    if (readOnly) return; // Prevent changes in read-only mode
    
    setFixedCosts(prev => prev.filter(cost => cost.id !== costId));
  };

  const handleFixedCostChange = (costId, field, value) => {
    if (readOnly) return; // Prevent changes in read-only mode
    
    setFixedCosts(prev => prev.map(cost => {
      if (cost.id === costId) {
        const updated = { ...cost, [field]: value };
        if (field === 'startMonth' || field === 'endMonth') {
          updated.duration = Math.max(0, (updated.endMonth || formData.adjustedMonths) - (updated.startMonth || 1) + 1);
        }
        return updated;
      }
      return cost;
    }));
  };

  const handleResetToDefaults = () => {
    if (readOnly) return; // Prevent changes in read-only mode
    
    if (window.confirm('This will reset all roles to the default configuration based on project size. Are you sure?')) {
      setFormData(prev => {
        const newData = {
          ...prev,
          isCustomized: false,
          useTeamRecommendation: true
        };
        
        return newData;
      });
      setTeamRecommendationExpanded(true);
      setStaggeredStartExpanded(true);
    }
  };

  const handleUnlockRecommendations = () => {
    if (readOnly) return;
    
    setFormData(prev => {
      const newData = {
        ...prev,
        useTeamRecommendation: true
      };
      
      return newData;
    });
    setTeamRecommendationExpanded(true);
    setStaggeredStartExpanded(true);
  };

  // Get timeline display for a role with actual dates
  const getRoleTimelineDisplay = (role) => {
    const effectiveRanges = getEffectiveDateRanges(role, 1, formData.adjustedMonths);
    const projectStart = dayjs(formData.start_month + '-01');
    
    if (role.useDefaultTimeline !== false) {
      // Default timeline - show actual dates
      const startDate = projectStart;
      const endDate = projectStart.add(formData.adjustedMonths - 1, 'month');
      return {
        display: `${startDate.format('MMM YYYY')} - ${endDate.format('MMM YYYY')}`,
        allocation: `${role.defaultAllocation || 100}%`,
        isDefault: true
      };
    } else {
      // Custom timeline
      const ranges = role.customDateRanges || [];
      if (ranges.length === 1) {
        const range = ranges[0];
        const startDate = projectStart.add(range.startMonth - 1, 'month');
        const endDate = projectStart.add(range.endMonth - 1, 'month');
        return {
          display: `${startDate.format('MMM YYYY')} - ${endDate.format('MMM YYYY')}`,
          allocation: `${range.allocation}%`,
          isDefault: false
        };
      } else {
        return {
          display: `${ranges.length} custom ranges`,
          allocation: 'Variable',
          isDefault: false
        };
      }
    }
  };

  // Check if role has timeline beyond header
  const roleExtendsBeforeHeader = (role) => {
    if (role.useDefaultTimeline !== false) return false;
    const ranges = role.customDateRanges || [];
    return ranges.some(range => range.startMonth < 1);
  };

  const roleExtendsBeyondHeader = (role) => {
    if (role.useDefaultTimeline !== false) return false;
    const ranges = role.customDateRanges || [];
    return ranges.some(range => range.endMonth > formData.adjustedMonths);
  };

  // Helper function to render rate with COLA indicator
  const renderRateWithIndicator = (role) => {
    const hasAdjustment = role.rateAdjustmentInfo;
    
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
          ${Math.round(role.rate || 0).toLocaleString()}/mo • {role.company} • {role.function}
        </Typography>
        {hasAdjustment && (
          <Tooltip
            title={
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                  COLA-Adjusted Rate
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  Base: ${Math.round(hasAdjustment.baseRate).toLocaleString()} ({hasAdjustment.baseYear})
                </Typography>
                <Typography variant="body2">
                  Adjusted: ${Math.round(role.rate).toLocaleString()} ({getProjectYear(outcomeData, formData)})
                </Typography>
                <Typography variant="body2">
                  COLA Rate: {hasAdjustment.colaRate}% annually
                </Typography>
              </Box>
            }
            arrow
          >
            <CalculateIcon 
              sx={{ 
                fontSize: '0.75rem', 
                color: 'warning.main',
                cursor: 'help'
              }} 
            />
          </Tooltip>
        )}
      </Box>
    );
  };

        // Recalculate summary when roles or costs change - memoized to prevent recalculation loops
  const calculatedSummary = useMemo(() => {
    if (!dataLoaded) return null;
    
    console.log('CostEstimationWizard: Recalculating summary due to data changes');
    console.log('CostEstimationWizard: Current roles:', formData.roles?.map(r => ({ name: r.name, allocation: r.defaultAllocation, rate: r.rate })));
    console.log('CostEstimationWizard: Current fixed costs:', fixedCosts?.map(c => ({ type: c.type, monthlyCost: c.monthlyCost, duration: c.duration })));
    
    const baseRoleCost = calculateTotalCurrentRoleCosts(formData);
    const totalFixedCost = Math.round((fixedCosts || []).reduce((sum, cost) => {
      return sum + (cost.monthlyCost || 0) * (cost.duration || formData.adjustedMonths);
    }, 0));

    console.log('CostEstimationWizard: Calculated costs - baseRoleCost:', baseRoleCost, 'totalFixedCost:', totalFixedCost);

    // Calculate CAPEX/OPEX amounts based on threshold and override
    const totalCost = baseRoleCost + totalFixedCost;
    let capexAmount, opexAmount;
    
    const isUnderThreshold = totalCost < CAPEX_THRESHOLD;
    if (isUnderThreshold && !formData.capexOverride) {
      // Under threshold - OPEX only with override option
      capexAmount = 0;
      opexAmount = totalCost;
    } else {
      // Over threshold or override enabled - use ratio
      capexAmount = Math.round(baseRoleCost * (formData.capexRatio / 100));
      opexAmount = Math.round(baseRoleCost * ((100 - formData.capexRatio) / 100) + totalFixedCost);
    }

    const monthlyBreakdown = calculateMonthlyBreakdown(formData, fixedCosts);

    const newSummary = {
      totalCost: Math.round(totalCost),
      baseRoleCost: Math.round(baseRoleCost),
      totalFixedCost: Math.round(totalFixedCost),
      capexAmount: Math.round(capexAmount),
      opexAmount: Math.round(opexAmount),
      capexRatio: formData.capexRatio,
      monthlyBreakdown,
      // Add role details for summary display
      roleDetails: (formData.roles || []).map(role => ({
        id: role.id,
        name: role.name,
        allocation: role.defaultAllocation || 100,
        rate: role.rate || 0,
        assignedMember: role.roster_member_id ? 
          itRoster.find(m => m.id === role.roster_member_id)?.name || 
          `${itRoster.find(m => m.id === role.roster_member_id)?.first_name || ''} ${itRoster.find(m => m.id === role.roster_member_id)?.last_name || ''}`.trim() || 
          'Unknown Member' : 
          null,
        monthlyCost: Math.round((role.rate || 0) * ((role.defaultAllocation || 100) / 100)),
        totalCost: Math.round((role.rate || 0) * ((role.defaultAllocation || 100) / 100) * formData.adjustedMonths)
      }))
    };
    
    console.log('CostEstimationWizard: New summary calculated:', newSummary);
    return newSummary;
  }, [rolesCostKey, fixedCostKey, formData.capexRatio, formData.capexOverride, formData.adjustedMonths, dataLoaded, itRoster]);
  
  // Update summary state when calculated summary changes
  useEffect(() => {
    setSummary(calculatedSummary);
  }, [calculatedSummary]);

  // Button style for better visibility in dark mode
  const localButtonStyle = buttonStyle || {
    backgroundColor: theme.palette.mode === 'dark' ? '#4db6ac' : undefined,
    color: theme.palette.mode === 'dark' ? theme.palette.common.black : undefined,
    '&:hover': {
      backgroundColor: theme.palette.mode === 'dark' ? '#00897b' : undefined,
    }
  };

  // Add save button for manual control when auto-save is disabled
  const renderSaveButton = () => {
    if (readOnly || autoSave) return null;
    
    return (
      <Button
        variant="outlined"
        onClick={handleManualSave}
        disabled={saving || hasValidationErrors()}
        startIcon={saving ? <CircularProgress size={16} /> : null}
        sx={{ mr: 1 }}
      >
        {saving ? 'Saving...' : 'Save Changes'}
      </Button>
    );
  };

  if (!dataLoaded) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
        <CircularProgress size={24} sx={{ mr: 2 }} />
        <Typography color="text.secondary">Loading cost estimation data...</Typography>
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box>
        <Box sx={{ mb: 1.5 }}>
          <Typography variant="subtitle2">
            Step 2 of 5: Cost Estimation
          </Typography>
          {formData.outcome_name && (
            <Typography variant="h6" color="primary" sx={{ mt: 0.5 }}>
              {formData.outcome_name}
            </Typography>
          )}
                    {saving && (
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
              <CircularProgress size={16} sx={{ mr: 1 }} />
              <Typography variant="caption" color="text.secondary">
                Saving changes...
              </Typography>
            </Box>
          )}
          {saveError && (
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
              <WarningIcon color="error" sx={{ fontSize: 16, mr: 1 }} />
              <Typography variant="caption" color="error">
                Auto-save failed. 
              </Typography>
              <Button 
                size="small" 
                onClick={() => {
                  setSaveError(false);
                  setAutoSaveDisabled(false);
                  if (hasDataChanged(formData)) {
                    debouncedUpdate(formData);
                  }
                }}
                sx={{ ml: 1, fontSize: '0.7rem', py: 0, px: 1 }}
              >
                Retry
              </Button>
            </Box>
          )}
          {autoSaveDisabled && (
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
              <WarningIcon color="warning" sx={{ fontSize: 16, mr: 1 }} />
              <Typography variant="caption" color="warning.main">
                Auto-save temporarily disabled due to render loop. 
              </Typography>
              <Button 
                size="small" 
                onClick={() => {
                  setAutoSaveDisabled(false);
                  setRenderCount(0);
                }}
                sx={{ ml: 1, fontSize: '0.7rem', py: 0, px: 1 }}
              >
                Re-enable
              </Button>
            </Box>
          )}
          {!autoSave && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              Auto-save is disabled. Use the Save button to save your changes.
            </Typography>
          )}
        </Box>

        {/* Team Recommendation Toggle */}
        <Card elevation={2} sx={{ mb: 2 }}>
          <CardContent sx={{ py: 1.5 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.useTeamRecommendation}
                  onChange={e => handleChange('useTeamRecommendation', e.target.checked)}
                  disabled={readOnly}
                />
              }
              label="Use Team Size Recommendation"
            />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontSize: '0.8rem' }}>
              When enabled, team composition is automatically set based on project size and confidence level.
            </Typography>
          </CardContent>
        </Card>

        {/* Team Recommendation Section - Collapsible */}
        {formData.useTeamRecommendation && (
          <Card elevation={2} sx={{ mb: 2 }}>
            <CardHeader
              title={
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant="h6">Team Size Recommendation</Typography>
                  {hasManualRoleChanges() && !teamRecommendationExpanded && (
                    <Chip 
                      icon={<LockIcon />} 
                      label="Locked - Manual Changes Detected" 
                      size="small" 
                      color="warning"
                      onClick={handleUnlockRecommendations}
                      sx={{ cursor: 'pointer' }}
                    />
                  )}
                  <IconButton
                    onClick={() => setTeamRecommendationExpanded(!teamRecommendationExpanded)}
                    disabled={readOnly}
                  >
                    {teamRecommendationExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </IconButton>
                </Box>
              }
              subheader={teamRecommendationExpanded ? "Define project size and confidence level" : `${formData.size} project, ${formData.confidence} confidence, ${formData.adjustedMonths} months`}
              sx={{ pb: 1 }}
            />
            <Collapse in={teamRecommendationExpanded}>
              <CardContent sx={{ pt: 0 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Project Size</InputLabel>
                      <Select
                        value={formData.size}
                        label="Project Size"
                        onChange={e => handleChange('size', e.target.value)}
                        disabled={readOnly}
                      >
                        {Object.keys(sizeDefs).map(sz => (
                          <MenuItem key={sz} value={sz}>
                            <Box>
                              <Typography variant="subtitle2">{sz} ({sizeMonths[sz]} months base)</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {sizeDefs[sz]}
                              </Typography>
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Confidence Level</InputLabel>
                      <Select
                        value={formData.confidence}
                        label="Confidence Level"
                        onChange={e => handleChange('confidence', e.target.value)}
                        disabled={readOnly}
                      >
                        {Object.keys(confidenceMultipliers).map(c => (
                          <MenuItem key={c} value={c}>
                            {c} (×{confidenceMultipliers[c]} duration)
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <DatePicker
                      label="Start Month"
                      views={['year', 'month']}
                      value={formData.start_month ? dayjs(formData.start_month + "-01") : dayjs()}
                      onChange={val => handleChange('start_month', val.format("YYYY-MM"))}
                      slotProps={{ textField: { fullWidth: true, size: 'small', disabled: readOnly } }}
                      disabled={readOnly}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <DatePicker
                      label="End Month"
                      views={['year', 'month']}
                      value={formData.start_month ? dayjs(formData.start_month + "-01").add(formData.adjustedMonths - 1, 'month') : dayjs().add(formData.adjustedMonths - 1, 'month')}
                      onChange={val => {
                        const startDate = dayjs(formData.start_month + "-01");
                        const newDuration = val.diff(startDate, 'month') + 1;
                        handleChange('adjustedMonths', Math.max(1, newDuration));
                      }}
                      slotProps={{ textField: { fullWidth: true, size: 'small', disabled: readOnly } }}
                      disabled={readOnly}
                    />
                  </Grid>
                </Grid>
                <Box sx={{ mt: 2, p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
                  <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                    <strong>Duration:</strong> {formData.adjustedMonths} months 
                    (Base: {formData.months}mo × {confidenceMultipliers[formData.confidence]} confidence)
                  </Typography>
                </Box>
              </CardContent>
            </Collapse>
          </Card>
        )}

        {/* Staggered Start Section - Collapsible */}
        {formData.useTeamRecommendation && (
          <Card elevation={2} sx={{ mb: 2 }}>
            <CardHeader
              title={
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant="h6">Staggered Start</Typography>
                  {hasManualRoleChanges() && !staggeredStartExpanded && (
                    <Chip 
                      icon={<LockIcon />} 
                      label="Locked" 
                      size="small" 
                      color="warning"
                    />
                  )}
                  <IconButton
                    onClick={() => setStaggeredStartExpanded(!staggeredStartExpanded)}
                    disabled={readOnly}
                  >
                    {staggeredStartExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </IconButton>
                </Box>
              }
              subheader={staggeredStartExpanded ? "Configure role start timing" : `Staggered start: ${formData.staggeredStart ? 'Enabled' : 'Disabled'}`}
              sx={{ pb: 1 }}
            />
            <Collapse in={staggeredStartExpanded}>
              <CardContent sx={{ pt: 0 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.staggeredStart}
                      onChange={e => handleChange('staggeredStart', e.target.checked)}
                      disabled={readOnly}
                    />
                  }
                  label="Staggered Start"
                />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontSize: '0.8rem' }}>
                  When enabled, Frontend, Backend, and QA roles will start 2 months after the project start date.
                </Typography>
              </CardContent>
            </Collapse>
          </Card>
        )}

        <Card elevation={2} sx={{ mb: 2 }}>
          <CardHeader
            title={
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="h6">Team Roles & Allocations</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  {/* Project Timeline Controls - Start/End Month */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <DatePicker
                      label="Start"
                      views={['year', 'month']}
                      value={formData.start_month ? dayjs(formData.start_month + "-01") : dayjs()}
                      onChange={val => handleHeaderDateChange('start_month', val.format("YYYY-MM"))}
                      slotProps={{ 
                        textField: { 
                          size: 'small', 
                          sx: { width: 120 },
                          disabled: readOnly 
                        } 
                      }}
                      disabled={readOnly}
                    />
                    <DatePicker
                      label="End"
                      views={['year', 'month']}
                      value={formData.start_month ? dayjs(formData.start_month + "-01").add(formData.adjustedMonths - 1, 'month') : dayjs().add(formData.adjustedMonths - 1, 'month')}
                      onChange={val => handleHeaderDateChange('end_month', val.format("YYYY-MM"))}
                      slotProps={{ 
                        textField: { 
                          size: 'small', 
                          sx: { width: 120 },
                          disabled: readOnly 
                        } 
                      }}
                      disabled={readOnly}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <DateRangeIcon color="action" fontSize="small" />
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                      {getOutcomeTimelineDisplay(formData.start_month, formData.adjustedMonths)}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            }
            subheader="Roles default to project timeline. Use the edit icon to customize individual role timelines."
            sx={{ pb: 1 }}
          />
          <CardContent sx={{ pt: 0 }}>
            {/* Add Member Button */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontSize: '0.9rem' }}>Add Member:</Typography>
              <Button
                variant="outlined"
                startIcon={<PersonAddIcon />}
                onClick={() => setAddMemberModalOpen(true)}
                sx={{ ...localButtonStyle, fontSize: '0.75rem', py: 0.25, mr: 2 }}
                disabled={readOnly}
              >
                Add Member
              </Button>
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontSize: '0.9rem' }}>Add Roles:</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {defaultRoles.map(roleName => (
                  <Button
                    key={roleName}
                    size="small"
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => handleAddRole(roleName)}
                    sx={{ ...localButtonStyle, fontSize: '0.75rem', py: 0.25 }}
                    disabled={readOnly}
                  >
                    {roleName}
                  </Button>
                ))}
              </Box>
            </Box>
            {formData.isCustomized && formData.useTeamRecommendation && (
              <Box sx={{ mb: 2, p: 1.5, bgcolor: 'action.hover', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                <Typography variant="body2" sx={{ mb: 1, fontSize: '0.8rem' }} color="text.secondary">
                  <strong>Custom Configuration:</strong> Roles have been customized and won't auto-update when project size changes.
                </Typography>
                <Button
                                    variant="outlined"
                  color="inherit"
                  onClick={handleResetToDefaults}
                  size="small"
                  disabled={readOnly}
                  sx={{
                    borderColor: 'divider',
                    fontSize: '0.75rem'
                  }}
                >
                  Reset to Defaults
                </Button>
              </Box>
            )}

                        {/* Roles Table */}
            {formData.roles && formData.roles.length > 0 ? (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell width="60px">Order</TableCell>
                      <TableCell>Role</TableCell>
                      <TableCell>Assignment</TableCell>
                      <TableCell>Timeline & Allocation</TableCell>
                      <TableCell>Rate & Details</TableCell>
                      <TableCell align="right">Monthly Cost</TableCell>
                      <TableCell align="right">Total Cost</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {formData.roles.map((role, roleIndex) => {
                      const timelineDisplay = getRoleTimelineDisplay(role);
                      const monthlyCost = (role.rate || 0) * ((role.defaultAllocation || 100) / 100);
                      const totalCost = monthlyCost * formData.adjustedMonths;
                      const memberOverAllocation = getMemberOverAllocation(role.roster_member_id);
                      
                      return (
                        <TableRow key={role.id}>
                          <TableCell>
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                              <Tooltip title="Move role up">
                                <IconButton
                                  size="small"
                                  onClick={() => handleMoveRole(role.id, 'up')}
                                  disabled={readOnly || roleIndex === 0}
                                  sx={{ p: 0.25 }}
                                >
                                  <MoveUpIcon sx={{ fontSize: '0.875rem' }} />
                                </IconButton>
                              </Tooltip>
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                {roleIndex + 1}
                              </Typography>
                              <Tooltip title="Move role down">
                                <IconButton
                                  size="small"
                                  onClick={() => handleMoveRole(role.id, 'down')}
                                  disabled={readOnly || roleIndex === formData.roles.length - 1}
                                  sx={{ p: 0.25 }}
                                >
                                  <MoveDownIcon sx={{ fontSize: '0.875rem' }} />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight={600}>
                              {role.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {role.function}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <MemoizedAutocomplete
                              role={role}
                              itRoster={itRoster}
                              onRosterMemberChange={(member) => {
                                handleRoleChange(
                                  role.id,
                                  'roster_member_id',
                                  member ? member.id : ''
                                );
                              }}
                              readOnly={readOnly}
                            />
                            {memberOverAllocation.hasOverAllocation && (
                              <Tooltip title={`Over-allocated: ${memberOverAllocation.totalAllocation}% across ${memberOverAllocation.roleCount} roles`}>
                                <WarningIcon color="warning" sx={{ fontSize: '1rem', mt: 0.5 }} />
                              </Tooltip>
                            )}
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Box>
                                <Typography variant="caption" color="text.secondary">
                                  {timelineDisplay.display}
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <TextField
                                    type="number"
                                    value={role.defaultAllocation || 100}
                                    onChange={(e) => handleRoleChange(role.id, 'allocation', Number(e.target.value))}
                                    size="small"
                                    inputProps={{ min: 0, max: 100, step: 5 }}
                                    sx={{ width: 70 }}
                                    disabled={readOnly}
                                  />
                                  <Typography variant="caption" color="text.secondary">%</Typography>
                                </Box>
                              </Box>
                              <IconButton
                                size="small"
                                onClick={() => handleEditRoleTimeline(role)}
                                disabled={readOnly}
                              >
                                <EditIcon sx={{ fontSize: '1rem' }} />
                              </IconButton>
                            </Box>
                          </TableCell>
                          <TableCell>
                            {renderRateWithIndicator(role)}
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight={600}>
                              ${Math.round(monthlyCost).toLocaleString()}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight={600}>
                              ${Math.round(totalCost).toLocaleString()}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                              ({formData.adjustedMonths} months)
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Tooltip title="Edit role settings">
                              <IconButton
                                size="small"
                                onClick={() => handleOpenEditRoleDialog(role)}
                                disabled={readOnly}
                              >
                                <SettingsIcon sx={{ fontSize: '1rem' }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Remove role">
                              <IconButton
                                size="small"
                                onClick={() => handleRemoveRole(role.id)}
                                disabled={readOnly}
                                color="error"
                              >
                                <DeleteIcon sx={{ fontSize: '1rem' }} />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Alert severity="info">
                No roles defined. Use the buttons above to add roles or enable team recommendations.
              </Alert>
            )}
          </CardContent>
        </Card>

        {/* Fixed Costs Section */}
        <Card elevation={2} sx={{ mt: 3 }}>
          <CardHeader
            title="Fixed Costs"
            action={
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={handleAddFixedCost}
                disabled={readOnly}
                size="small"
              >
                Add Fixed Cost
              </Button>
            }
          />
          <CardContent>
            {fixedCosts && fixedCosts.length > 0 ? (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Type</TableCell>
                      <TableCell>Details</TableCell>
                      <TableCell>Monthly Cost</TableCell>
                      <TableCell>Duration (months)</TableCell>
                      <TableCell align="right">Total Cost</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {fixedCosts.map((cost) => (
                      <TableRow key={cost.id}>
                        <TableCell>
                          <TextField
                            value={cost.type || ''}
                            onChange={(e) => handleFixedCostChange(cost.id, 'type', e.target.value)}
                            size="small"
                            disabled={readOnly}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            value={cost.details || ''}
                            onChange={(e) => handleFixedCostChange(cost.id, 'details', e.target.value)}
                            size="small"
                            disabled={readOnly}
                            multiline
                            maxRows={2}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            type="number"
                            value={cost.monthlyCost || 0}
                            onChange={(e) => handleFixedCostChange(cost.id, 'monthlyCost', Number(e.target.value))}
                            size="small"
                            disabled={readOnly}
                            inputProps={{ min: 0 }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            type="number"
                            value={cost.duration || 0}
                            onChange={(e) => handleFixedCostChange(cost.id, 'duration', Number(e.target.value))}
                            size="small"
                            disabled={readOnly}
                            inputProps={{ min: 0, max: formData.adjustedMonths }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight={600}>
                            ${((cost.monthlyCost || 0) * (cost.duration || 0)).toLocaleString()}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <IconButton
                            size="small"
                            onClick={() => handleRemoveFixedCost(cost.id)}
                            disabled={readOnly}
                            color="error"
                          >
                            <DeleteIcon sx={{ fontSize: '1rem' }} />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Alert severity="info">
                No fixed costs defined. Click "Add Fixed Cost" to add licenses, tools, or other fixed expenses.
              </Alert>
            )}
          </CardContent>
        </Card>

        {/* Summary Cards */}
        {summary && (
          <SummaryCards
            summary={summary}
            formData={formData}
            isUnderCapexThreshold={isUnderCapexThreshold()}
            onCapexRatioChange={(value) => handleChange('capexRatio', value)}
            onCapexOverrideChange={(value) => handleChange('capexOverride', value)}
            readOnly={readOnly}
          />
        )}

        {/* Action Buttons */}
        {!hideButtons && (
          <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
            {renderSaveButton()}
            {onSave && (
              <Button
                variant="contained"
                onClick={onSave}
                disabled={readOnly || saving || hasValidationErrors()}
                sx={localButtonStyle}
              >
                {saving ? 'Saving...' : 'Save & Continue'}
              </Button>
            )}
          </Box>
        )}

        {/* Modals */}
        <TimelineAdjustmentModal
          open={timelineModalOpen}
          onClose={() => {
            setTimelineModalOpen(false);
            setPendingTimelineChange(null);
          }}
          onConfirm={handleTimelineAdjustmentConfirm}
          pendingChange={pendingTimelineChange}
        />

        <RoleTimelineModal
          open={roleTimelineModalOpen}
          onClose={() => {
            setRoleTimelineModalOpen(false);
            setEditingRole(null);
          }}
          role={editingRole}
          projectMonths={formData.adjustedMonths}
          onSave={handleSaveRoleTimeline}
          readOnly={readOnly}
        />

        <AddMemberModal
          open={addMemberModalOpen}
          onClose={() => setAddMemberModalOpen(false)}
          onMemberAdded={handleMemberAdded}
        />

        {/* Add Role Dialog */}
        <Dialog open={addRoleDialogOpen} onClose={() => setAddRoleDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Add New Role</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Role Name</InputLabel>
                  <Select
                    value={newRoleForm.name}
                    label="Role Name"
                    onChange={(e) => handleNewRoleFormChange('name', e.target.value)}
                  >
                    {defaultRoles.map(role => (
                      <MenuItem key={role} value={role}>{role}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Company</InputLabel>
                  <Select
                    value={newRoleForm.company}
                    label="Company"
                    onChange={(e) => handleNewRoleFormChange('company', e.target.value)}
                  >
                    {Object.keys(locationOptions).map(company => (
                      <MenuItem key={company} value={company}>{company}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Location</InputLabel>
                  <Select
                    value={newRoleForm.location}
                    label="Location"
                    onChange={(e) => handleNewRoleFormChange('location', e.target.value)}
                  >
                    {(locationOptions[newRoleForm.company] || []).map(location => (
                      <MenuItem key={location.code} value={location.code}>
                        {location.display}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth size="small" required>
                  <InputLabel>Specific Role *</InputLabel>
                  <Select
                    value={newRoleForm.role}
                    label="Specific Role *"
                    onChange={(e) => handleNewRoleFormChange('role', e.target.value)}
                    error={!newRoleForm.role}
                  >
                    {(roleOptions[newRoleForm.company]?.[newRoleForm.location] || []).map(role => (
                      <MenuItem key={role} value={role}>{role}</MenuItem>
                    ))}
                  </Select>
                  {!newRoleForm.role && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, fontSize: '0.75rem' }}>
                      Specific role is required
                    </Typography>
                  )}
                </FormControl>
              </Grid>
              {newRoleForm.rate > 0 && (
                <Grid item xs={12}>
                  <Box sx={{ 
                    p: 2, 
                    bgcolor: 'background.paper', 
                    border: '1px solid', 
                    borderColor: 'divider', 
                    borderRadius: 1 
                  }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Monthly Rate (Auto-calculated)
                    </Typography>
                    <Typography variant="h6" color="primary.main">
                      ${Math.round(newRoleForm.rate).toLocaleString()}/month
                    </Typography>
                    {newRoleForm.rateAdjustmentInfo && (
                      <Typography variant="caption" color="warning.main">
                        COLA-adjusted rate
                      </Typography>
                    )}
                  </Box>
                </Grid>
              )}
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAddRoleDialogOpen(false)}>Cancel</Button>
            <Button 
              onClick={handleAddRoleFromDialog} 
              variant="contained"
              disabled={!newRoleForm.role || !newRoleForm.role.trim()}
            >
              Add Role
            </Button>
          </DialogActions>
        </Dialog>

        {/* Edit Role Dialog */}
        <Dialog open={editRoleDialogOpen} onClose={() => setEditRoleDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Edit Role Metadata</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Role Name</InputLabel>
                  <Select
                    value={editRoleForm.name}
                    label="Role Name"
                    onChange={(e) => handleEditRoleFormChange('name', e.target.value)}
                  >
                    {defaultRoles.map(role => (
                      <MenuItem key={role} value={role}>{role}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Company</InputLabel>
                  <Select
                    value={editRoleForm.company}
                    label="Company"
                    onChange={(e) => handleEditRoleFormChange('company', e.target.value)}
                  >
                    {Object.keys(locationOptions).map(company => (
                      <MenuItem key={company} value={company}>{company}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Location</InputLabel>
                  <Select
                    value={editRoleForm.location}
                    label="Location"
                    onChange={(e) => handleEditRoleFormChange('location', e.target.value)}
                  >
                    {(locationOptions[editRoleForm.company] || []).map(location => (
                      <MenuItem key={location.code} value={location.code}>
                        {location.display}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Specific Role</InputLabel>
                  <Select
                    value={editRoleForm.role}
                    label="Specific Role"
                    onChange={(e) => handleEditRoleFormChange('role', e.target.value)}
                  >
                    {(roleOptions[editRoleForm.company]?.[editRoleForm.location] || []).map(role => (
                      <MenuItem key={role} value={role}>{role}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Monthly Rate"
                  type="number"
                  value={editRoleForm.rate}
                  onChange={(e) => handleEditRoleFormChange('rate', Number(e.target.value))}
                  fullWidth
                  size="small"
                  InputProps={{
                    startAdornment: '$'
                  }}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditRoleDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveEditedRole} variant="contained">
              Save Changes
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
}

export default CostEstimationWizard;